import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { RiRefreshLine } from 'react-icons/ri';
import { IoSearchOutline } from 'react-icons/io5';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import LeaseApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import { canEditPosting } from './revenuePostingHelpers';

const PAGE_SIZE = 20;
const DATE_API = 'YYYY-MM-DD';
const REVENUE_SCREEN = '/Property/Management/Lease/Revenue/Posting';
const STATUS_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Approved', value: 'Approved' },
];
const BADGE = { display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 };
const BADGE_GREEN = { ...BADGE, background: '#DCF5E4', color: '#1C7C3C' };
const BADGE_AMBER = { ...BADGE, background: '#FFF1D6', color: '#9A6700' };
const NO_EDIT_TIP = 'Approved postings cannot be changed.';

const defaultDates = () => ({
    fromDate: moment().startOf('month').format(DATE_API),
    toDate: moment().format(DATE_API),
});

// Filters + page live in the URL; missing dates fall back to this month.
const readFilters = (sp) => {
    const defaults = defaultDates();
    return {
        postingNo: sp.get('postingNo') || '',
        voucherNo: sp.get('voucherNo') || '',
        propertyNo: sp.get('propertyNo') || '',
        unitNo: sp.get('unitNo') || '',
        leaseNo: sp.get('leaseNo') || '',
        status: sp.get('status') || '',
        fromDate: sp.has('fromDate') ? sp.get('fromDate') : defaults.fromDate,
        toDate: sp.has('toDate') ? sp.get('toDate') : defaults.toDate,
    };
};
const toDateObj = (value) => (value ? moment(value, DATE_API).toDate() : null);
const dateFormatter = (p) => (p.value ? moment(p.value).format('DD-MMM-YYYY') : '');
const moneyFormatter = (p) => {
    const v = parseFloat(p.value);
    return Number.isNaN(v) ? '' : v.toFixed(3);
};
const errorOf = (error) => ({
    status: error?.response?.status,
    message: error?.response?.data?.message || error?.message,
});

const ActionCell = ({ data, onView, onEdit, onDelete }) => {
    const allowed = canEditPosting(data);
    const btn = { background: 'none', border: 'none', padding: '0 6px', cursor: 'pointer' };
    const off = { ...btn, opacity: 0.35, cursor: 'not-allowed' };
    return (
        <div className="d-flex align-items-center h-100">
            <button type="button" style={btn} title="View" aria-label="View" onClick={() => onView(data)}><FaEye /></button>
            <button type="button" style={allowed ? btn : off} title={allowed ? 'Edit' : NO_EDIT_TIP} aria-label="Edit"
                disabled={!allowed} onClick={() => onEdit(data)}><FaEdit /></button>
            <button type="button" style={allowed ? { ...btn, color: '#C0392B' } : off} title={allowed ? 'Delete' : NO_EDIT_TIP} aria-label="Delete"
                disabled={!allowed} onClick={() => onDelete(data)}><FaTrash /></button>
        </div>
    );
};

const LeaseRevenuePostingList = () => {
    const apiService = useMemo(() => new LeaseApiService(), []);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const applied = useMemo(() => readFilters(searchParams), [searchParams]);
    const pageNo = Number(searchParams.get('pageNo')) || 1;

    const [form, setForm] = useState(applied);
    const [properties, setProperties] = useState([]);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [approvedDialog, setApprovedDialog] = useState(false);

    useEffect(() => { setForm(readFilters(searchParams)); }, [searchParams]);

    useEffect(() => {
        const params = new URLSearchParams();
        params.append('dropdownTypes', 'LRPSerPropertyName');
        apiService.getApiParamater('GET_REVENUE_MULTI', `?${params.toString()}`)
            .then((res) => setProperties(res?.data?.dropdowns?.LRPSerPropertyName || []))
            .catch((e) => console.error('Error loading properties:', e));
    }, [apiService]);

    // Fetches whenever the URL (filters / page) changes or a reload is asked for.
    useEffect(() => {
        let stale = false;
        setLoading(true);
        apiService.getLeaseRevenuePostings({ ...applied, pageNo, pageSize: PAGE_SIZE })
            .then((res) => {
                if (stale) return;
                // 204 = feature off for this client (or nothing to show): no toast
                const body = res?.status === 204 ? null : res?.data;
                setRows(Array.isArray(body?.result) ? body.result : []);
                setPagination(body?.pagination || body?.paginationData || null);
            })
            .catch((e) => {
                if (stale) return;
                setRows([]);
                setPagination(null);
                toast.error(errorOf(e).message || 'Unable to load the postings.');
            })
            .finally(() => { if (!stale) setLoading(false); });
        return () => { stale = true; };
    }, [apiService, applied, pageNo, reloadKey]);

    const setField = (key) => (value) => setForm((p) => ({ ...p, [key]: value }));
    const writeUrl = useCallback((filters, page) => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) next.set(k, v); });
        // an emptied date must stay empty instead of falling back to the default
        if (!filters.fromDate) next.set('fromDate', '');
        if (!filters.toDate) next.set('toDate', '');
        if (page > 1) next.set('pageNo', String(page));
        setSearchParams(next);
    }, [setSearchParams]);

    const handleSearch = () => {
        if (form.toDate && !form.fromDate) { toast.warning('Pick a From date.'); return; }
        writeUrl(form, 1);
        setReloadKey((k) => k + 1);
    };
    const handleReset = () => {
        setSearchParams(new URLSearchParams());
        setReloadKey((k) => k + 1);
    };
    const handlePage = (page) => writeUrl(applied, page);

    const openPosting = useCallback((row, mode) => {
        const q = new URLSearchParams({ postingNo: String(row.postingNo) });
        if (mode === 'view') q.set('mode', 'view');
        navigate(`${REVENUE_SCREEN}?${q.toString()}`);
    }, [navigate]);

    const handleDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            const res = await apiService.deleteLeaseRevenuePosting(toDelete.postingNo);
            if (res?.status === 204) { setToDelete(null); return; }
            toast.success(res?.data?.message || 'Posting deleted successfully.');
            setToDelete(null);
            setReloadKey((k) => k + 1);
        } catch (e) {
            const { status, message } = errorOf(e);
            setToDelete(null);
            if (status === 409) setApprovedDialog(true);
            else toast.error(message || 'Unable to delete the posting.');
        } finally {
            setDeleting(false);
        }
    };

    const columnDefs = useMemo(() => [
        { headerName: 'Posting No', field: 'postingNo', width: 120, pinned: 'left', filter: true },
        { headerName: 'Voucher No', field: 'voucherNo', width: 140, filter: true },
        {
            headerName: 'Period', width: 200,
            valueGetter: (p) => (p.data?.periodFrom ? `${moment(p.data.periodFrom).format('DD-MMM-YYYY')} – ${moment(p.data.periodTo).format('DD-MMM-YYYY')}` : ''),
        },
        { headerName: 'Posting Date', field: 'postingDate', width: 130, valueFormatter: dateFormatter },
        { headerName: 'Debit Account', field: 'debitAcName', width: 220, filter: true },
        { headerName: 'Credit Account', field: 'creditAcName', width: 220, filter: true },
        { headerName: 'Lines', field: 'lineCount', width: 90, type: 'rightAligned', headerClass: 'ag-right-aligned-header' },
        { headerName: 'Total Amount', field: 'totalAmount', width: 150, type: 'rightAligned', headerClass: 'ag-right-aligned-header', valueFormatter: moneyFormatter },
        {
            headerName: 'Status', field: 'isApproved', width: 120,
            cellRenderer: (p) => (p.data ? <span style={p.data.isApproved ? BADGE_GREEN : BADGE_AMBER}>{p.data.isApproved ? 'Approved' : 'Pending'}</span> : null),
        },
        { headerName: 'Modified By', field: 'modifiedBy', width: 150, filter: true },
        { headerName: 'Modified On', field: 'modifiedOn', width: 150, valueFormatter: dateFormatter },
        {
            headerName: 'Action', width: 130, pinned: 'right', sortable: false, filter: false,
            cellRenderer: (p) => (p.data ? <ActionCell data={p.data} onView={(r) => openPosting(r, 'view')} onEdit={(r) => openPosting(r, 'edit')} onDelete={setToDelete} /> : null),
        },
    ], [openPosting]);

    const propertyOptions = (properties || []).map((c) => ({ value: c?.value, label: c?.text }));
    const selectedProperty = propertyOptions.find((o) => o.value === form.propertyNo) || null;
    const selectedStatus = STATUS_OPTIONS.find((o) => o.value === form.status) || STATUS_OPTIONS[0];
    const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

    return (
        <div className="p-3">
            <div className="cost-center-section mb-3">
                <div className="d-flex slide-header"><div className="h6 pt-1">Lease Revenue Postings</div></div>
                <div className="border-box2 p-3">
                    <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(200px, 1fr))', gap: 15 }}>
                        <input className="p-2 form-control" placeholder="Posting No" value={form.postingNo} onChange={(e) => setField('postingNo')(e.target.value)} />
                        <input className="p-2 form-control" placeholder="Voucher No" value={form.voucherNo} onChange={(e) => setField('voucherNo')(e.target.value)} />
                        <WindowedSelect className="p-0" isClearable placeholder="Property" menuPortalTarget={document.body} styles={selectStyles}
                            value={selectedProperty} options={propertyOptions} onChange={(o) => setField('propertyNo')(o?.value || '')} />
                        <input className="p-2 form-control" placeholder="Unit No" value={form.unitNo} onChange={(e) => setField('unitNo')(e.target.value)} />
                        <input className="p-2 form-control" placeholder="Lease No" value={form.leaseNo} onChange={(e) => setField('leaseNo')(e.target.value)} />
                        <WindowedSelect className="p-0" menuPortalTarget={document.body} styles={selectStyles}
                            value={selectedStatus} options={STATUS_OPTIONS} onChange={(o) => setField('status')(o?.value || '')} />
                        <DatePicker selected={toDateObj(form.fromDate)} dateFormat="dd-MMM-yyyy" isClearable placeholderText="From Date" className="p-2 form-control w-100"
                            onChange={(d) => setField('fromDate')(d ? moment(d).format(DATE_API) : '')} />
                        <DatePicker selected={toDateObj(form.toDate)} dateFormat="dd-MMM-yyyy" isClearable placeholderText="To Date" className="p-2 form-control w-100"
                            minDate={toDateObj(form.fromDate)} onChange={(d) => setField('toDate')(d ? moment(d).format(DATE_API) : '')} />
                        <div className="d-flex gap-3" style={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
                            {loading ? (
                                <div className="mainbtnGry w-25"><ClipLoader color="#ffffff" className="mt-1" size={25} /></div>
                            ) : (
                                <div className="mainbtnGry flex-end gap-2" role="button" tabIndex={0} onClick={handleSearch}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}>
                                    <IoSearchOutline color="#fff" className="center mt-1 mx-2" />
                                    <div className="center">Search</div>
                                </div>
                            )}
                            <div className="mainbtnRed flex-end gap-2" role="button" tabIndex={0} onClick={handleReset}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleReset(); }}>
                                <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                <div className="center">Reset</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="gridshad mt-1">
                {!loading && rows.length === 0 ? (
                    <div className="text-muted p-4">No revenue postings found for the selected filters.</div>
                ) : (
                    <div className="ag-theme-quartz" style={{ height: 560 }}>
                        <AgGridReact
                            rowData={rows}
                            columnDefs={columnDefs}

                            getRowStyle={(p) => ({ background: p.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                            suppressMovableColumns
                        />
                    </div>
                )}
                {pagination && <Pagination pagination={pagination} getItems={handlePage} />}
            </div>

            <Modal size="md" show={!!toDelete} onHide={() => !deleting && setToDelete(null)} backdrop="static">
                <Modal.Header closeButton><Modal.Title className="center">Confirmation</Modal.Title></Modal.Header>
                <Modal.Body>Delete posting {toDelete?.postingNo}? This removes its voucher as well.</Modal.Body>
                <Modal.Footer className="end p-3">
                    <button className="mainbtnGry" disabled={deleting} onClick={() => setToDelete(null)}>Cancel</button>
                    <button className="mainbtnRed" disabled={deleting} onClick={handleDelete}>{deleting ? 'Deleting…' : 'Delete'}</button>
                </Modal.Footer>
            </Modal>

            <Modal size="md" show={approvedDialog} backdrop="static" keyboard={false}>
                <Modal.Header><Modal.Title className="center">Posting approved</Modal.Title></Modal.Header>
                <Modal.Body>This posting has been approved and can no longer be changed.</Modal.Body>
                <Modal.Footer className="end p-3">
                    <button className="mainbtn" onClick={() => { setApprovedDialog(false); setReloadKey((k) => k + 1); }}>Reload</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeaseRevenuePostingList;
